import { Component, createApp, Plugin } from 'vue';
import { Router } from 'vue-router';
import { attachSentryToApp } from '../../Plugins/sentry';
import { callWhenReady, experiment } from '../../Plugins/amplitude';

export type ComponentLoadingParams = {
    plugins: Array<Plugin | [Plugin, Record<string, any>]>;
    component: () => Promise<{default: Component}>;
    router?: () => Router;
    featureFlag?: string;
    featureFlagInverted?: boolean;
    preserveContent?: boolean;
}

const checkFeatureFlag = (featureFlag: string, inverted: boolean = false): Promise<boolean> => {
    return new Promise((resolve) => {
        callWhenReady(() => {
            const variant = experiment?.variant(featureFlag);
            const isEnabled = variant?.value === 'on';
            resolve(inverted ? !isEnabled : isEnabled);
        });
    });
};

export const mountComponents = async (components: Record<string, ComponentLoadingParams>) => {
    for (const [name, {plugins, component, router, featureFlag, featureFlagInverted, preserveContent}] of Object.entries(components)) {
        const mountPoints = document.querySelectorAll(`[data-component="${name}"]`);

        mountPoints.forEach(async mountPoint => {
            let preservedHTML = '';
            if (preserveContent) {
                preservedHTML = mountPoint.innerHTML;
            }

            const propsAttr = mountPoint.getAttribute('data-props');
            const propsData = propsAttr ? JSON.parse(propsAttr) : {};

            if (preserveContent) {
                propsData.content = preservedHTML;
            }

            if (featureFlag) {
                checkFeatureFlag(featureFlag, featureFlagInverted).then(shouldMount => {
                    if (!shouldMount) {
                        mountPoint.remove();
                        return;
                    }
                    mountComponent(mountPoint, component, plugins, router, propsData);
                });
            } else {
                mountComponent(mountPoint, component, plugins, router, propsData);
            }
        });
    }
};

const mountComponent = async (
    mountPoint: Element,
    component: () => Promise<{default: Component}>,
    plugins: Array<Plugin | [Plugin, Record<string, any>]>,
    router: (() => Router) | undefined,
    propsData: Record<string, any>
) => {
    const { default: Component } = await component();
    const app = createApp(Component, propsData);

    plugins.forEach(plugin => {
        if (Array.isArray(plugin)) {
            return app.use(...plugin);
        }
        app.use(plugin);
    });
    if (router) {
        app.use(router());
    }
    attachSentryToApp(app);
    app.mount(mountPoint);
};
